import { useState } from "react";
import React from "react";
import { Toaster } from "sonner";
import { ArrowUpRight } from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import { PRICING_FAQS } from "../data/pricingContent";
import { PricingCompareTable } from "../components/landing/PricingCompareTable";
import { PricingPurchaseDialog } from "../components/landing/PricingPurchaseDialog";
import { ContactSalesDialog } from "../components/contact/ContactSalesDialog";
import { ModuleFeaturesSection } from "../components/pricing/ModuleFeaturesSection";
import { EveryPlanIncludesSection } from "../components/pricing/EveryPlanIncludesSection";

const isContactSalesTier = (tier) =>
    tier?.monthly === null || tier?.isEnterprise;

export default function PricingPage() {
    const yearly = true;
    const [purchaseOpen, setPurchaseOpen] = useState(false);
    const [contactSalesOpen, setContactSalesOpen] = useState(false);
    const [selectedTier, setSelectedTier] = useState(null);

    const openContactSales = () => {
        setContactSalesOpen(true);
    };

    const openPurchaseForm = (tier) => {
        if (isContactSalesTier(tier)) {
            openContactSales();
            return;
        }
        setSelectedTier(tier);
        setPurchaseOpen(true);
    };

    return (
        <div className="pt-28 lg:pt-32" data-testid="pricing-page">
            <Toaster position="top-right" theme="dark" richColors />
            {/* Hero */}
            <section className="relative overflow-hidden py-16 lg:py-20">
                <div className="absolute inset-0 grid-bg opacity-[0.2] pointer-events-none" />
                <div className="absolute inset-0 radial-glow pointer-events-none" />
                <div className="relative max-w-5xl mx-auto px-6 sm:px-8 text-center">
                    <p className="font-code text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-4">
                        // pricing · modules
                    </p>
                    <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.06]">
                        One platform.{" "}
                        <span className="text-zinc-500">
                            Every feature on every plan.
                        </span>
                    </h1>
                    <p className="mt-5 max-w-2xl mx-auto text-zinc-400 font-light leading-relaxed">
                        Dashboard, Sales, HR, Projects, Finance, Documents,
                        Communications, Analytics &amp; AI — full access on every
                        plan. Compare license bands and pick what fits your team.
                    </p>
                </div>
            </section>

            {/* Pricing table */}
            <section className="relative pb-16">
                <div className="max-w-7xl mx-auto px-6 sm:px-8">
                    <PricingCompareTable
                        yearly={yearly}
                        testId="pricing-page-table"
                        onTierCta={openPurchaseForm}
                    />

                    <PricingPurchaseDialog
                        open={purchaseOpen}
                        onOpenChange={setPurchaseOpen}
                        tier={selectedTier}
                        yearly={yearly}
                        onContactSales={openContactSales}
                    />

                    <ContactSalesDialog
                        open={contactSalesOpen}
                        onOpenChange={setContactSalesOpen}
                    />

                    <div className="mt-10 rounded-2xl border border-white/[0.08] bg-obsidian-200 p-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h3 className="font-display text-lg font-semibold text-white">
                                Need more than 350 licenses?
                            </h3>
                            <p className="text-sm text-zinc-400 mt-1 max-w-xl font-light">
                                Enterprise plans include dedicated account
                                management, custom storage, procurement-ready
                                contracts and a 99.99% SLA.
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={openContactSales}
                            className="shrink-0 inline-flex items-center gap-2 text-sm font-semibold text-white bg-white/5 border border-white/10 hover:bg-white/10 px-5 py-2.5 rounded-full transition"
                            data-testid="pricing-contact-sales-cta"
                        >
                            Contact sales
                            <ArrowUpRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </section>

            <ModuleFeaturesSection
                eyebrow="// module features"
                title="Full feature set"
                titleAccent="on every plan."
                description="Nine modules power six departments. Starter, Business and Enterprise all include every feature below — plans differ only by license count."
                compareLink="/compare"
                testId="pricing-module-features"
            />

            <EveryPlanIncludesSection testId="pricing-every-plan" />

            {/* FAQ */}
            <section className="relative py-16 lg:py-20 border-t border-white/[0.06]">
                <div className="max-w-3xl mx-auto px-6 sm:px-8">
                    <div className="text-center mb-10">
                        <p className="font-code text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-3">
                            // pricing faq
                        </p>
                        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-white">
                            Common questions
                        </h2>
                    </div>
                    <Accordion
                        type="single"
                        collapsible
                        className="divide-y divide-white/[0.06] border-y border-white/[0.06]"
                        data-testid="pricing-faq-accordion"
                    >
                        {PRICING_FAQS.map((f, i) => (
                            <AccordionItem
                                key={i}
                                value={`p-${i}`}
                                className="border-0"
                            >
                                <AccordionTrigger className="text-left py-5 font-display text-base font-semibold text-white hover:no-underline">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-zinc-400 text-sm leading-relaxed font-light pb-5">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>
        </div>
    );
}
